import axios, {AxiosResponse} from "axios";
import {useEffect, useState} from "react";
import {UserProfile} from "../components/settings/UserManagement/UserProfile";
import {SERVICES_API_URL} from "../config";
import {useOktaAuth} from "@okta/okta-react";

type TenantServiceProps = {
  url: string;
};

export interface Tenant {
  id: string;
  shortName: string;
  hubName: string;
  legacyCmsUrl: string;
}

export interface SiteConfig {
  configId: number;
  configKey: string;
  configValue: string;
}

export interface JobEverCompleteStatusRequest {
  jobIds: number[];
}

export interface JobEverCompleteStatusItem {
  jobId: number;
  everCompleted: boolean;
}

export type JobEverCompleteStatusResponse = JobEverCompleteStatusItem[];

const useTenantService = ({url}: TenantServiceProps) => {
  const [data, setData] = useState<Tenant[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error>();
  const {oktaAuth, authState} = useOktaAuth();

  useEffect(() => {
    const fetchData = (token: string) => {
      const controller = new AbortController();
      const signal = controller.signal;

      axios
        .get(`${SERVICES_API_URL}/tenants${url}`, {
          signal: signal,
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((response) => {
          setLoading(false);
          setData(response.data);
        })
        .catch((e) => {
          controller.abort();
          setError(e);
        });
    };

    const token = oktaAuth.getAccessToken();
    if (token && authState?.isAuthenticated) {
      fetchData(token);
    }
  }, [authState?.isAuthenticated, oktaAuth, url]);

  return {error, loading, data};
};

const getTenant = (token: string, tenantId: string): Promise<AxiosResponse> => {
  return axios.get(`${SERVICES_API_URL}/tenants/tenant/${tenantId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const switchTenants = (token: string, tenantId: string): Promise<AxiosResponse> => {
  return axios.get(`${SERVICES_API_URL}/tenants/switch/${tenantId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const createUser = (token: string, user: UserProfile): Promise<AxiosResponse> => {
  return axios.post(`${SERVICES_API_URL}/tenants/users/create`, user, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const updateUser = (token: string, user: UserProfile): Promise<AxiosResponse> => {
  return axios.post(`${SERVICES_API_URL}/tenants/users/update`, user, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const deleteUser = (token: string, user: UserProfile): Promise<AxiosResponse> => {
  return axios.post(`${SERVICES_API_URL}/tenants/users/disable/${user.id}`, null, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const resendActivation = (token: string, userId: string): Promise<AxiosResponse> => {
  return axios.post(`${SERVICES_API_URL}/tenants/users/reactivate/${userId}`, null, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const getLegacyUserGuid = (token: string): Promise<AxiosResponse> => {
  return axios.get(`${SERVICES_API_URL}/tenants/getLegacyUserGuid`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const generateTemporaryPassword = (token: string, userId: string): Promise<AxiosResponse> => {
  return axios.post(`${SERVICES_API_URL}/tenants/users/expire-password-with-temp-password/${userId}`, null, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const getSiteConfig = (token: string, siteId: number, configKey: string): Promise<AxiosResponse<SiteConfig>> => {
  return axios.get(`${SERVICES_API_URL}/tenants/siteConfig`, {
    params: {
      siteId,
      configKey
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const getJobEverCompleteStatus = (
  token: string,
  request: JobEverCompleteStatusRequest
): Promise<AxiosResponse<JobEverCompleteStatusResponse>> => {
  return axios.post(`${SERVICES_API_URL}/tenants/getJobEverCompleteStatus`, request, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const getConfig = (token: string, configKey: string): Promise<AxiosResponse<{items: SiteConfig[]}>> => {
  return axios.get(`${SERVICES_API_URL}/tenants/config`, {
    params: {
      configKey
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

export {
  useTenantService,
  getTenant,
  switchTenants,
  getLegacyUserGuid,
  createUser,
  updateUser,
  deleteUser,
  resendActivation,
  generateTemporaryPassword,
  getSiteConfig,
  getJobEverCompleteStatus,
  getConfig
};
